"use client"

/**
 * Live preview surface for /tools/fertilizer-calculator.
 * Bag count + NPK split + 12-month feeding calendar + "why this amount" panel.
 */

import type { FertilizerPlan } from "@/lib/fertilizer-plan"
import {
  YearCalendar,
  type YearCalendarCell,
} from "@/components/tools/shared/year-calendar"

const F_BG_ALT = "#f9fafb"
const F_INK = "#111827"
const F_INK2 = "#374151"
const F_RULE = "#e5e7eb"
const F_BRAND = "#16a34a"
const F_BRAND_700 = "#15803d"
const F_BRAND_100 = "#dcfce7"
const F_MUTED = "#6b7280"
const F_MUTED2 = "#9ca3af"

const N_COLOR = "#22c55e"
const P_COLOR = "#a855f7"
const K_COLOR = "#f59e0b"

export interface FertilizerPreviewProps {
  plan: FertilizerPlan
  compact?: boolean
}

export function FertilizerPreview({ plan, compact = false }: FertilizerPreviewProps) {
  const today = new Date()
  const thisMonth = today.getMonth()

  if (compact) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: `1px solid ${F_RULE}`,
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          padding: 14,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: F_BRAND_700,
            marginBottom: 6,
          }}
        >
          Apply per feeding
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
          <span
            style={{
              fontSize: 26,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.025em",
              color: F_INK,
            }}
          >
            {plan.productLbs.toFixed(1)}
          </span>
          <span style={{ fontSize: 13, color: F_MUTED }}>lbs</span>
          <span style={{ fontSize: 12, color: F_MUTED, marginLeft: "auto" }}>{plan.type.npk}</span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: F_MUTED,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <BagIcon size={12} />
          {plan.bags < 1
            ? `~${Math.round(plan.bags * 100)}% of a 40-lb bag`
            : `${plan.bags.toFixed(1)} × 40-lb bags`}
        </div>
        <YearCalendar
          variant="compact"
          months={toFertilizerCells(plan)}
          thisMonth={thisMonth}
          ringColors={{ today: F_INK2 }}
          ariaLabel="12-month feeding calendar"
        />
      </div>
    )
  }

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${F_RULE}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
        padding: "24px 24px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 20,
        color: F_INK,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: F_BRAND_700,
            marginBottom: 8,
          }}
        >
          Apply per feeding
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 38,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.025em",
              color: F_INK,
            }}
          >
            {plan.productLbs.toFixed(1)}
          </span>
          <span style={{ fontSize: 14, color: F_MUTED }}>lbs of product</span>
        </div>
        <div
          style={{
            fontSize: 13,
            color: F_MUTED,
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <BagIcon size={13} />
          {plan.bags < 1
            ? `About ${Math.round(plan.bags * 100)}% of a ${plan.bagSize}-lb bag`
            : `${plan.bags.toFixed(1)} bags @ ${plan.bagSize} lb`}
          <span style={{ color: F_MUTED2 }}>·</span>
          <span>{plan.type.npk} blend</span>
        </div>
      </div>

      <div style={{ height: 1, background: F_RULE }} />

      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: F_INK2,
            marginBottom: 12,
          }}
        >
          This application delivers
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          <NutrientStat letter="N" name="Nitrogen" lbs={plan.npk.n} color={N_COLOR} />
          <NutrientStat letter="P" name="Phosphorus" lbs={plan.npk.p} color={P_COLOR} />
          <NutrientStat letter="K" name="Potassium" lbs={plan.npk.k} color={K_COLOR} />
        </div>
      </div>

      <div style={{ height: 1, background: F_RULE }} />

      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: F_INK2,
            marginBottom: 12,
          }}
        >
          When to feed
        </div>
        <YearCalendar
          variant="full"
          months={toFertilizerCells(plan)}
          thisMonth={thisMonth}
          ringColors={{ today: F_INK2 }}
          todayDot
          ariaLabel="12-month feeding calendar"
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginTop: 12,
            fontSize: 12,
            color: F_MUTED,
          }}
        >
          <span>{plan.appsPerYear} applications / year</span>
          <span style={{ color: F_MUTED2 }}>·</span>
          <span>{plan.annualNlbs.toFixed(1)} lb N total</span>
        </div>
      </div>

      <div style={{ height: 1, background: F_RULE }} />

      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: F_INK2,
            marginBottom: 10,
          }}
        >
          Why this amount
        </div>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <Reason>
            {plan.grass.name} wants{" "}
            <strong style={{ color: F_INK }}>{plan.grass.annualN} lb N</strong> per 1,000 sqft per
            year.
          </Reason>
          <Reason>
            <strong style={{ color: F_INK }}>{plan.season.label}</strong> rate is{" "}
            {Math.round(plan.season.factor * 100)}% of standard.
          </Reason>
          <Reason>
            Goal &ldquo;{plan.goal.name.toLowerCase()}&rdquo; uses{" "}
            {Math.round(plan.goal.factor * 100)}% rate.
            {plan.soil.mod !== 1.0 && ` ${plan.soil.name} soil ×${plan.soil.mod}.`}
          </Reason>
        </ul>
      </div>
    </div>
  )
}

function toFertilizerCells(plan: FertilizerPlan): YearCalendarCell[] {
  return plan.months.map(m => ({
    i: m.i,
    label: m.label,
    fill: m.on ? F_BRAND : F_BG_ALT,
  }))
}

function NutrientStat({
  letter,
  name,
  lbs,
  color,
}: {
  letter: string
  name: string
  lbs: number
  color: string
}) {
  return (
    <div
      style={{
        background: F_BG_ALT,
        borderRadius: 8,
        padding: "10px 12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 9,
            background: color,
            color: "#fff",
            fontSize: 10,
            fontWeight: 800,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {letter}
        </span>
        <span style={{ fontSize: 11, color: F_MUTED, fontWeight: 500 }}>{name}</span>
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: F_INK,
          letterSpacing: "-0.015em",
        }}
      >
        {lbs.toFixed(2)}{" "}
        <span style={{ fontSize: 11, color: F_MUTED, fontWeight: 500 }}>lb</span>
      </div>
    </div>
  )
}

function Reason({ children }: { children: React.ReactNode }) {
  return (
    <li
      style={{
        display: "flex",
        gap: 10,
        fontSize: 13.5,
        color: F_INK2,
        lineHeight: 1.55,
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          flexShrink: 0,
          marginTop: 1,
          borderRadius: 9,
          background: F_BRAND_100,
          color: F_BRAND_700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckIcon size={11} />
      </span>
      <span>{children}</span>
    </li>
  )
}

function BagIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 8h10l1 12a1 1 0 01-1 1H7a1 1 0 01-1-1l1-12z" />
      <path d="M9 8V5a3 3 0 016 0v3" />
    </svg>
  )
}

function CheckIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12l4 4 10-10" />
    </svg>
  )
}
